import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { App } from 'antd';
import {
  CloseOutlined, DownloadOutlined, FolderOpenOutlined, HistoryOutlined,
  LoadingOutlined, CheckCircleFilled, CloseCircleFilled, MessageOutlined,
  SearchOutlined, DeleteOutlined,
} from '@ant-design/icons';
import {
  getWorkSessions, getRecentTasks, getFileDownloadUrl, deleteWorkSession, CollabTask, WorkSession,
} from '../../services/teamCollabApi';
import { useAuthStore } from '../../stores/authStore';
import { DEPTS } from './office-config';

/* ============================================================
   会话档案（智能工坊的"会话"在指挥办公室的体现）
   每次进入指挥办公室交办的智力工作都记在一个会话里；
   这里按会话翻阅历史任务与成果——领导视角的"工作台账"。
   大字号：领导要看大字。
   ============================================================ */

/** 从指令前缀【部门名】解析部门（dispatchWorkshop 派发时写入的） */
const deptFromCommand = (command: string) => {
  const m = command.match(/^【([^】]{2,12})】/);
  if (!m) return null;
  return DEPTS.find((d) => d.name === m[1] || d.aliases?.includes(m[1])) || null;
};

const fmtTime = (ts?: string) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PAGE_SIZE = 12;

const SessionArchive: React.FC<{
  currentSessionId: string | null;
  onContinueSession: (session: WorkSession) => void;
  onClose: () => void;
}> = ({ currentSessionId, onContinueSession, onClose }) => {
  const { modal, message } = App.useApp();
  const [sessions, setSessions] = useState<WorkSession[] | null>(null);
  const [selSession, setSelSession] = useState<string | 'all'>('all');
  const [tasks, setTasks] = useState<CollabTask[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  /* 关键字搜索：输入即原始值，防抖后才落到 query 触发请求 */
  const [kw, setKw] = useState('');
  const [query, setQuery] = useState('');
  const authToken = useAuthStore((s) => s.token);
  const selectedSession = selSession === 'all' ? null : sessions?.find((s) => s.id === selSession) || null;
  const reqSeq = useRef(0);

  const loadSessions = useCallback(() => {
    getWorkSessions().then((list) => {
      setSessions((list || []).filter((s) => (s.taskCount ?? 1) > 0));
    }).catch(() => setSessions([]));
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  /* 防抖 400ms：少于 2 个字符不搜（空则清空搜索），避免每敲一下就打后台 */
  useEffect(() => {
    const t = setTimeout(() => {
      const q = kw.trim();
      setQuery(q.length >= 2 ? q : '');
    }, 400);
    return () => clearTimeout(t);
  }, [kw]);

  /* 切会话/换搜索词回第一页 */
  useEffect(() => { setPage(0); }, [selSession, query]);

  useEffect(() => {
    setTasks(null);
    setOpenTaskId(null);
    const seq = ++reqSeq.current;
    /* 多取 1 条探测下一页；search 走后台 LIKE(已分页 take=13)，不会拉全表 */
    getRecentTasks(page * PAGE_SIZE, PAGE_SIZE + 1, query || undefined, selSession === 'all' ? undefined : selSession)
      .then((list) => {
        if (seq !== reqSeq.current) return;   // 只认最新一次请求，丢弃过期返回
        setHasMore((list || []).length > PAGE_SIZE);
        setTasks((list || []).slice(0, PAGE_SIZE));
      })
      .catch(() => { if (seq === reqSeq.current) { setTasks([]); setHasMore(false); } });
  }, [selSession, page, query]);

  const removeSession = useCallback((s: WorkSession) => {
    modal.confirm({
      className: 'df-readable-modal',
      title: '删除这个会话？',
      content: '删除后会话入口移除，历史任务仍会保留在「全部工作」里，可继续查阅。',
      okText: '删除', okButtonProps: { danger: true }, cancelText: '取消',
      onOk: async () => {
        try {
          await deleteWorkSession(s.id);
          if (selSession === s.id) setSelSession('all');
          loadSessions();
          message.success('会话已删除');
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || '删除失败');
        }
      },
    });
  }, [modal, message, selSession, loadSessions]);

  const download = useCallback(async (t: CollabTask) => {
    if (!t.resultFileId) return;
    try {
      const res = await fetch(getFileDownloadUrl(t.resultFileId), {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.resultFileName || '产物文件';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e: any) {
      window.alert(e?.message || '下载失败');
    }
  }, [authToken]);

  return (
    <div className="aco19-wrap" onClick={onClose}>
      <div className="aco19-panel" onClick={(e) => e.stopPropagation()}>
        <header>
          <HistoryOutlined className="ic" />
          <div className="tt">
            <b>会话档案</b>
            <span>按会话翻阅成果，也可以接着历史上下文继续提问</span>
          </div>
          <div className="aco19-search" onClick={(e) => e.stopPropagation()}>
            <SearchOutlined />
            <input
              value={kw}
              placeholder="搜索对话关键字（≥2字）…"
              onChange={(e) => setKw(e.target.value)}
            />
            {kw && <button className="clr" onClick={() => setKw('')}>✕</button>}
          </div>
          <button className="x" onClick={onClose}><CloseOutlined /></button>
        </header>
        <div className="body">
          {/* 左：会话列表 */}
          <aside className="sessions">
            <button className={`srow ${selSession === 'all' ? 'on' : ''}`} onClick={() => setSelSession('all')}>
              <FolderOpenOutlined className="fi" />
              <span><b>全部工作</b><em>最近 60 条任务</em></span>
            </button>
            {sessions === null && <div className="empty"><LoadingOutlined /> 正在调档…</div>}
            {sessions?.map((s) => (
              <div
                key={s.id}
                className={`srow ${selSession === s.id ? 'on' : ''} ${currentSessionId === s.id ? 'current' : ''}`}
                role="button"
                onClick={() => setSelSession(s.id)}
              >
                <FolderOpenOutlined className="fi" />
                <span>
                  <b>{s.title || '未命名会话'}{currentSessionId === s.id && <i className="curmark">当前</i>}</b>
                  <em>{typeof s.taskCount === 'number' ? `${s.taskCount} 项任务 · ` : ''}{fmtTime(s.createdAt)}</em>
                </span>
                <button
                  className="del"
                  title="删除会话"
                  onClick={(e) => { e.stopPropagation(); removeSession(s); }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
            {sessions?.length === 0 && <div className="empty">还没有会话记录</div>}
          </aside>

          {/* 右：任务台账（分页） */}
          <div className="ledger">
            {selectedSession && (
              <div className={`aco19-resume ${currentSessionId === selectedSession.id ? 'active' : ''}`}>
                <MessageOutlined />
                <span>
                  <b>{currentSessionId === selectedSession.id ? '当前正在使用这个会话' : '需要沿着这次工作继续问？'}</b>
                  <em>接续后，通用智能体的口头汇报、文档和表格任务会结合最近 5 轮上下文，不用复制旧内容。</em>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onContinueSession(selectedSession);
                    onClose();
                  }}
                >
                  {currentSessionId === selectedSession.id ? '回到输入框' : '继续此会话'}
                </button>
              </div>
            )}
            {tasks === null && <div className="empty"><LoadingOutlined /> {query ? '正在搜索…' : '正在翻阅台账…'}</div>}
            {tasks?.length === 0 && page === 0 && (
              <div className="empty">{query ? `没有包含「${query}」的对话` : '这个会话里还没有任务'}</div>
            )}
            {tasks?.map((t) => {
              const dept = deptFromCommand(t.command || '');
              const open = openTaskId === t.id;
              const ok = t.status === 'completed';
              const bad = t.status === 'failed';
              return (
                <div key={t.id} className={`trow ${open ? 'open' : ''}`} style={dept ? ({ '--acc': dept.accent } as React.CSSProperties) : undefined}>
                  <button className="thead" onClick={() => setOpenTaskId(open ? null : t.id)}>
                    {ok ? <CheckCircleFilled className="st ok" /> : bad ? <CloseCircleFilled className="st bad" /> : <LoadingOutlined className="st run" />}
                    {dept && <i className="tag">{dept.name}</i>}
                    <b>{(t.command || '').replace(/^【[^】]*】/, '').slice(0, 60)}</b>
                    <em>{fmtTime(t.createdAt)}</em>
                  </button>
                  {open && (
                    <div className="tbody">
                      {bad && <div className="err">{t.result || '任务执行失败'}</div>}
                      {!bad && (
                        <div className="md">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.result || t.summary || '（暂无正文成果）'}</ReactMarkdown>
                        </div>
                      )}
                      {t.resultFileId && (
                        <button className="dl" onClick={() => download(t)}>
                          <DownloadOutlined /> 下载 {t.resultFileName || '产物文件'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* 分页条 */}
            {tasks !== null && (page > 0 || hasMore) && (
              <div className="pager">
                <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ 上一页</button>
                <em>第 {page + 1} 页</em>
                <button disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>下一页 ›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionArchive;
